import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/App.css';
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Container } from 'react-bootstrap'
import GlobalSidebar from './components/GlobalSidebar';
import Landing from 'pages/Landing';
import RouteBoundary from 'components/RouteBoundary';
import { AuthProvider } from 'context/AuthContext';
import { dismissHero } from 'utils/boot';
import { lazyRoute } from 'utils/lazyRoute';

// Each page on its own (above all, the map libraries only where they're used), so that the start
// page has little to load.
const MapRevamp = lazyRoute(() => import('pages/MapRevamp'));
const MapExplorer = lazyRoute(() => import('pages/MapExplorer'));
const BestPeaks = lazyRoute(() => import('pages/BestPeaks'));
const Ranges = lazyRoute(() => import('pages/Ranges'));
const Login = lazyRoute(() => import('pages/Login'));
const Upload = lazyRoute(() => import('pages/Upload'));
const Gallery = lazyRoute(() => import('pages/Gallery'));
const SetMediaLocation = lazyRoute(() => import('pages/SetMediaLocation'));
const About = lazyRoute(() => import('pages/About'));
const Picture = lazyRoute(() => import('components/Picture'));
const Place = lazyRoute(() => import('components/Place'));
const PeakCropEditor = lazyRoute(() => import('pages/PeakCropEditor'));
const Peak = lazyRoute(() => import('components/Peak'));

/** The start script's photo is for the panorama: on the way to any other page it goes. */
const BootHeroGuard: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname !== '/' && !pathname.startsWith('/place/')) dismissHero();
  }, [pathname]);
  return null;
};

const Boundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  return <RouteBoundary pathname={pathname}>{children}</RouteBoundary>;
};

const App: React.FC = () => (
  <BrowserRouter>
    <BootHeroGuard />
    <AuthProvider>
      <GlobalSidebar />
      <Container>
        <Boundary>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/map" element={<MapRevamp />} />
          <Route path="/map-revamp" element={<Navigate to="/map" replace />} />
          <Route path="/map-classic" element={<MapExplorer />} />
          <Route path="/best-peaks" element={<BestPeaks />} />
          <Route path="/login" element={<Login />} />
          <Route path="/ranges/:rangeId" element={<Ranges />}/>
          <Route path="/peak/:peakId" element={<Peak />}/>
          <Route path="/picture/:id" element={<Picture />}/>
          <Route path="/picture/:id/peak/:peakId/crop" element={<PeakCropEditor />}/>
          <Route path="/place/:id" element={<Place />}/>
          <Route path="/upload" element={<Upload />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/gallery/:year" element={<Gallery />} />
          <Route path="/gallery/:year/:month" element={<Gallery />} />
          <Route path="/gallery/:year/:month/:day" element={<Gallery />} />
          <Route path="/media/:mediaId/set-location" element={<SetMediaLocation />} />
          <Route path="/media/set-location" element={<SetMediaLocation />} />
          <Route path="/about" element={<About />} />
        </Routes>
        </Boundary>
      </Container>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
